const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/reducers/labRepInstanceReducer.ts';
import Imm from 'immutable';
import { IActionHandlersMap, makeReducer } from '../defs/actionTypes';
import * as LabDefs from '../defs/labReportDefs';
import { DEFAULT_INIT_AVAIL_LIMIT_NUM_DAYS } from '../defs/labReportDefs';
import { actionTypes, Reload1CumulReportAction } from '../actions/labReportsActions';
import {
    actionTypes as uiActionTypes,
    SelectLabReportAction,
    SetCheckedLabResultsAction,
    SetOptionsAction,
    SetSuppressSnackbarsAction,
    SetVisibleMaxDatAction,
    ToggleLabResultCheckedAction,
} from '../actions/labReportsUiActions';
import { loadCumulSummaryHandlers } from './labReports/loadCumulSummaryHandlers';
import { loadCumulDataHandlers } from './labReports/loadCumulDataHandlers';
import {
    abortLoadingCumulSummaryHandler,
    aboutToLoadCumulSummaryHandler,
    completedLoadingCumulSummaryHandler,
    extendCumulAvailLimitHandler,
    forget1CumulReportHandler,
    setDesiredNumColumnsHandler,
    setFetchOptionsHandler,
    truncate1CumulReportHandler,
} from './labReports/generalHandlers';
import { KISIM_TIME_OPEN } from '../defs/generalDefs';

export const initialState = Imm.fromJS({
    // Maps patnr_defsId to cumulative report data.
    cumulReports: {},

    // Defines how far into the past data will be fetched initially.
    initFetchInterval: {
        amount: DEFAULT_INIT_AVAIL_LIMIT_NUM_DAYS,
        units: 'days',
    },

    // Options that change how data is fetched
    fetchOptions: {},

    // These values define the current state of certain UI elements.
    ui: {
        mode: LabDefs.TABLE_MODE,
        numColumns: 0, // 0=auto
        requiredFlags: [], // empty=all
        selected: {},
        checked: Imm.OrderedMap<string, any>(),
        visible: { maxdat: KISIM_TIME_OPEN },
        suppressSnackbars: false,
    },
});

const handlers: IActionHandlersMap = {
    [actionTypes.loadCumulSummary.pending]: loadCumulSummaryHandlers.pending,
    [actionTypes.loadCumulSummary.success]: loadCumulSummaryHandlers.success,
    [actionTypes.loadCumulSummary.failure]: loadCumulSummaryHandlers.failure,

    [actionTypes.loadCumulData.pending]: loadCumulDataHandlers.pending,
    [actionTypes.loadCumulData.success]: loadCumulDataHandlers.success,
    [actionTypes.loadCumulData.failure]: loadCumulDataHandlers.failure,

    [actionTypes.aboutToLoadCumulSummary]: aboutToLoadCumulSummaryHandler,
    [actionTypes.completedLoadingCumulSummary]: completedLoadingCumulSummaryHandler,
    [actionTypes.abortLoadingCumulSummary]: abortLoadingCumulSummaryHandler,
    [actionTypes.extendCumulAvailLimit]: extendCumulAvailLimitHandler,
    [actionTypes.setDesiredNumColumns]: setDesiredNumColumnsHandler,
    [actionTypes.setFetchOptions]: setFetchOptionsHandler,
    [actionTypes.truncate1CumulReport]: truncate1CumulReportHandler,
    [actionTypes.forget1CumulReport]: forget1CumulReportHandler,

    [actionTypes.reload1CumulReport]: (state, action: Reload1CumulReportAction) => {
        state = forget1CumulReportHandler(state, action);

        // It is not guaranteed there will be any data at visibleMaxDat after reload. In fact, there
        // won't be any when the user has scrolled far into the past, because the saga won't reload
        // that part. As a simple workaround, we always reset visibleMaxDat when reloading.
        return state.setIn(['ui', 'visible'], initialState.getIn(['ui', 'visible']));
    },

    [uiActionTypes.launchLabReportsPage]: state => {
        return state
            .setIn(['ui', 'mode'], initialState.getIn(['ui', 'mode']))
            .setIn(['ui', 'numColumns'], initialState.getIn(['ui', 'numColumns']))
            .setIn(['ui', 'selected'], initialState.getIn(['ui', 'selected']))
            .setIn(['ui', 'checked'], initialState.getIn(['ui', 'checked']))
            .setIn(['ui', 'visible'], initialState.getIn(['ui', 'visible']))
            .setIn(['ui', 'suppressSnackbars'], initialState.getIn(['ui', 'suppressSnackbars']));
    },

    [uiActionTypes.selectLabReport]: (state, action: SelectLabReportAction) => {
        return state
            .setIn(['ui', 'selected', 'defsId'], action.defsId)
            .setIn(['ui', 'mode'], initialState.getIn(['ui', 'mode']))
            .setIn(['ui', 'checked'], initialState.getIn(['ui', 'checked']))
            .setIn(['ui', 'visible'], initialState.getIn(['ui', 'visible']));
    },

    [uiActionTypes.setOptions]: (state, action: SetOptionsAction) => {
        return state //
            .setIn(['ui', 'numColumns'], action.numColumns || 0)
            .setIn(['ui', 'requiredFlags'], Imm.fromJS(action.requiredFlags || []))
            .setIn(['fetchOptions', 'mapIcode'], action.mapIcode || '');
    },

    [uiActionTypes.setVisibleMaxDat]: (state, action: SetVisibleMaxDatAction) => {
        if (!action.maxdat) {
            state = state.setIn(['ui', 'visible'], initialState.getIn(['ui', 'visible']));
        } else {
            state = state.setIn(['ui', 'visible', 'maxdat'], action.maxdat);
        }

        return state;
    },

    [uiActionTypes.setCheckedLabResults]: (state, action: SetCheckedLabResultsAction) => {
        state = state.setIn(['ui', 'checked'], initialState.getIn(['ui', 'checked']));

        if (action.rowIds) {
            action.rowIds.forEach((rowId: string) => {
                state = state.setIn(['ui', 'checked', rowId], true);
            });
        }

        return state;
    },

    [uiActionTypes.toggleLabResultChecked]: (state, action: ToggleLabResultCheckedAction) => {
        if (state.getIn(['ui', 'checked', action.rowId])) {
            // The checkmark is currently checked. Remove the checkmark!
            state = state.deleteIn(['ui', 'checked', action.rowId]);
        } else {
            const checked = state.getIn(['ui', 'checked']) || initialState.getIn(['ui', 'checked']);

            if (typeof action.pos !== 'number') {
                // Prepend the new checkmark at the beginning of the ordered map.
                state = state.setIn(
                    ['ui', 'checked'],
                    Imm.OrderedMap<string, any>().set(action.rowId, true).concat(checked)
                );
            } else {
                // Insert the checkmark at the given position in the ordered map.
                state = state.setIn(
                    ['ui', 'checked'],
                    checked
                        .slice(0, action.pos + 1)
                        .set(action.rowId, true)
                        .concat(checked.slice(action.pos + 1))
                );
            }
        }

        return state;
    },

    [uiActionTypes.setSuppressSnackbars]: (state, action: SetSuppressSnackbarsAction) => {
        return state.setIn(['ui', 'suppressSnackbars'], !!action.suppress);
    },
};

export const labRepInstanceReducer = makeReducer(handlers, initialState);
